import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, TemplateRef } from "@angular/core";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  ActivityService,
  AuthService,
  BaseStrategyService,
  BrandAssetsService,
  DspService,
  ELIGIBLE_AUDIENCE_BOOST_CAMPAIGN_TYPES,
  fblMode,
  FblModeSize,
  getVideoDealIds,
  SbStrategyService,
  UserSelectionService,
} from "@front/m19-services";

import {
  IButtonDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  ITab,
  ITabsComponent,
  ITooltipDirective,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { FblModeComponent } from "@m19-board/strategies/fbl-mode/fbl-mode.component";
import { combineLatestWith, filter, Observable, of, startWith } from "rxjs";
import { map, switchMap, withLatestFrom } from "rxjs/operators";

import { AsyncPipe, DatePipe } from "@angular/common";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { RouterLink, RouterOutlet } from "@angular/router";
import {
  AccountMarketplace,
  AccountType,
  AlgoMode,
  BidderRun,
  CampaignType,
  Currency,
  DSPStrategy,
  EntityIdType,
  HistoryActionEnum,
  StrategyDspCreativeType,
  StrategyDspFunnel,
  StrategyStateEnum,
  StrategyType,
  TacticType,
} from "@front/m19-api-client";
import { CAMPAIGN_TYPE, StrategyService } from "@front/m19-injection-token";
import {
  CampaignTypeWithTacos,
  CurrencySymbols,
  DspFunnelDescriptions,
  Marketplaces,
  StrategyEx,
  StrategyTypeStr,
} from "@front/m19-models";
import { DspStrategyEx } from "@front/m19-models/strategy/DspStrategyEx";
import { StrategyFromCT } from "@front/m19-models/strategy/StrategyBuilder";
import { DateUtils, Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActivityEventAnnotationsService } from "@m19-board/activities/activity-event-annotations.service";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { SpendProgressBarComponent } from "@m19-board/shared/spend-progress-bar/spend-progress-bar.component";
import { StateBadgeComponent } from "@m19-board/shared/state-badge/state-badge.component";
import { StrategyInfoBlocComponent } from "@m19-board/shared/strategy-info-bloc/strategy-info-bloc.component";
import { StrategyPageAlgoTargetComponent } from "@m19-board/strategies/strategy-page/strategy-page-algo-target/strategy-page-algo-target.component";
import { StrategyPageDspFunnelComponent } from "@m19-board/strategies/strategy-page/strategy-page-dsp-funnel/strategy-page-dsp-funnel.component";
import { StrategyPageWarningComponent } from "@m19-board/strategies/strategy-page/strategy-page-warning/strategy-page-warning.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { SyncInfoComponent } from "@m19-board/strategies/sync-info/sync-info.component";
import { StrategyPageDspCreativeTypeComponent } from "./strategy-page-dsp-creative-type/strategy-page-dsp-creative-type.component";
import { SwitchAlgoModeComponent } from "./switch-algo-mode-popup/switch-algo-mode.component";

enum Section {
  PRODUCTS = "products",
  AD_LINES = "ad-lines",
  AUDIENCES = "audiences",
  SD_TARGETINGS = "targetings",
  TACTICS = "tactics",
  ADVANCED_SETTINGS = "advanced-settings",
  TARGETING_STATS = "targeting-stats",
  STRATEGY_STATS = "stats",
  ACTIVITIES = "activities",
  CREATIVES = "creatives",
  SUPPLY = "supply",
  AUDIENCE_STATS = "audience-stats",
  BUSINESS_STATS = "business-stats",
}

@Component({
  selector: "app-strategy-page",
  templateUrl: "./strategy-page.component.html",
  imports: [
    StateBadgeComponent,
    TranslocoDirective,
    ITooltipDirective,
    AsyncPipe,
    StrategyInfoBlocComponent,
    FblModeComponent,
    SpendProgressBarComponent,
    EditableTextComponent,
    ITabsComponent,
    NavBreadcrumbDirective,
    RouterOutlet,
    SyncInfoComponent,
    DatePipe,
    IButtonDirective,
    RouterLink,
    IFormFieldComponent,
    IIconComponent,
    ILabelComponent,
    IInputDirective,
    FormsModule,
    StrategyPageDspFunnelComponent,
    StrategyPageWarningComponent,
    StrategyPageAlgoTargetComponent,
    StrategyPageDspCreativeTypeComponent,
  ],
  providers: [StrategyPageService, ModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageComponent<T extends CampaignType> {
  private readonly strategyPageService = inject<StrategyPageService<T>>(StrategyPageService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly accountMarketplaceSelection = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly toastrService = inject(ToasterService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly activityService = inject(ActivityService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject<BaseStrategyService<T>>(StrategyService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly campaignType = inject(CAMPAIGN_TYPE);
  private readonly dspService = inject(DspService);

  strategy = this.strategyPageService.strategy;

  strategyGroup = this.strategyPageService.strategyGroup;

  isProductOrLegacyStrategy = computed(() =>
    [StrategyType.PRODUCT, StrategyType.LEGACY].includes(this.strategy()!.strategyType),
  );
  withAlgoMode = computed(() => this.campaignType !== CampaignType.DSP);

  readonly tabs = computed<ITab<Section>[]>(() => {
    const strategy = this.strategy();
    if (!strategy) return [];

    return [
      {
        label: this.translocoService.translate("common.products"),
        value: Section.PRODUCTS,
        icon: "package",
        hidden: this.campaignType === CampaignType.SB,
        url: Section.PRODUCTS,
      },
      {
        label: this.translocoService.translate("sb-page.ad_lines_all"),
        value: Section.AD_LINES,
        icon: "creatives",
        hidden: this.campaignType !== CampaignType.SB,
        url: Section.AD_LINES,
      },
      {
        label: this.translocoService.translate("common.audiences"),
        value: Section.AUDIENCES,
        icon: "audience",
        hidden:
          this.campaignType !== CampaignType.DSP ||
          !!this.dealWithoutAudienceTargeting() ||
          ((<DSPStrategy>strategy).dspFunnel === StrategyDspFunnel.AWARENESS &&
            (<DSPStrategy>strategy).creativeType !== StrategyDspCreativeType.VIDEO_DEAL),
        url: Section.AUDIENCES,
      },
      {
        label: this.translocoService.translate("sd-strategy-creation.targetings"),
        value: Section.SD_TARGETINGS,
        icon: "targeting",
        hidden: this.campaignType !== CampaignType.SD,
        testId: "sd-targetings",
        url: Section.SD_TARGETINGS,
      },
      {
        label: this.translocoService.translate("strategy-page.tactics"),
        value: Section.TACTICS,
        icon: "tactics",
        hidden:
          this.campaignType !== CampaignType.SB &&
          (this.campaignType !== CampaignType.SP || !this.isProductOrLegacyStrategy()),
        testId: "tactics-tab",
        url: Section.TACTICS,
      },
      {
        label: this.translocoService.translate("page-title.creatives"),
        value: Section.CREATIVES,
        hidden: this.campaignType !== CampaignType.DSP,
        icon: "creatives",
        url: Section.CREATIVES,
      },
      {
        label: this.translocoService.translate("page-title.supply"),
        value: Section.SUPPLY,
        hidden: this.campaignType !== CampaignType.DSP,
        icon: "globe",
        url: Section.SUPPLY,
      },
      {
        label: this.translocoService.translate("sb-page.targeting_stats"),
        value: Section.TARGETING_STATS,
        icon: "targetingStats",
        hidden:
          this.campaignType &&
          this.campaignType !== CampaignTypeWithTacos.TACOS &&
          [CampaignType.SD, CampaignType.DSP].includes(this.campaignType!),
        url: Section.TARGETING_STATS,
      },
      {
        label: this.translocoService.translate("strategy-page.strategy_stats"),
        value: Section.STRATEGY_STATS,
        icon: "chartLine",
        url: Section.STRATEGY_STATS,
      },
      {
        label: this.translocoService.translate("common.audience_stats"),
        value: Section.AUDIENCE_STATS,
        icon: "audience",
        hidden:
          (this.campaignType !== CampaignTypeWithTacos.TACOS &&
            !ELIGIBLE_AUDIENCE_BOOST_CAMPAIGN_TYPES.has(this.campaignType)) ||
          !this.strategy()?.boostAudienceId,
        url: Section.AUDIENCE_STATS,
      },
      {
        label: this.translocoService.translate("amazon-business-boost.title"),
        value: Section.BUSINESS_STATS,
        icon: "chartLine",
        hidden: !(strategy.isSpStrategy() && strategy.businessBoostEnabled),
        url: Section.BUSINESS_STATS,
      },
      {
        label: this.translocoService.translate("page-title.activities"),
        value: Section.ACTIVITIES,
        icon: "clock",
        url: Section.ACTIVITIES,
      },

      {
        label: this.translocoService.translate("advanced-settings-modal.advanced_settings"),
        value: Section.ADVANCED_SETTINGS,
        icon: "advancedSettings",
        hidden: !this.displayAdvancedSettingsSection(),
        testId: "adv-settings",
        url: Section.ADVANCED_SETTINGS,
      },
    ];
  });

  strategy$: Observable<StrategyFromCT<T>> = toObservable(this.strategy).pipe(
    filter((s): s is StrategyFromCT<T> => !!s),
  );

  accountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  isReadOnly = toSignal(this.accountMarketplaceSelection.readOnlyMode$, { requireSync: true });
  isSingleStrategyInStrategyGroup = this.strategyPageService.isSingleStrategyInStrategyGroup;

  // A programmatic guaranteed deal fixes the budget and the flight dates.
  programmaticGuaranteedDeal = toSignal(
    this.campaignType !== CampaignType.DSP
      ? of(undefined)
      : this.strategy$.pipe(
          switchMap((strategy) =>
            this.dspService.getProgrammaticGuaranteedDeal(
              this.accountMarketplace().dspAdvertiserId!,
              getVideoDealIds((strategy as DspStrategyEx).dspSupply),
            ),
          ),
        ),
  );

  // Amazon forbids audiences on a PG deal and on a Netflix deal not enabled for Amazon audiences.
  dealWithoutAudienceTargeting = toSignal(
    this.campaignType !== CampaignType.DSP
      ? of(false)
      : this.strategy$.pipe(
          switchMap((strategy) =>
            this.dspService.hasDealWithoutAudienceTargeting(
              this.accountMarketplace().dspAdvertiserId!,
              getVideoDealIds((strategy as DspStrategyEx).dspSupply),
            ),
          ),
        ),
  );

  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  private bidderRuns: Signal<BidderRun[] | undefined> = toSignal(
    this.accountMarketplaceService.listBidderRuns(
      this.accountMarketplace().accountId,
      this.accountMarketplace().marketplace,
      this.accountMarketplace().dspAdvertiserId,
    ),
  );
  history = toSignal(
    this.strategy$.pipe(
      switchMap((s) =>
        this.activityService.getLastTwoDaysHistory(
          this.activityEventAnnotationsService.getStrategyActivityFilter(s, this.creatives() ?? []),
        ),
      ),
    ),
  );
  infoMessageLastUpdate = computed<string | undefined>(() => {
    const history = this.history();
    if (!history || history.length == 0) {
      return undefined;
    }
    const accountMarketplace = this.accountMarketplace();
    const lastUpdate = history
      .filter((a) => a && a.timestamp)
      .sort((a, b) => b.timestamp!.localeCompare(a.timestamp!))[0];
    const lastUpdateIsStrategyCreation =
      lastUpdate.action === HistoryActionEnum.create && lastUpdate.primaryType === EntityIdType.strategyId;
    const bidderRun: BidderRun | undefined = this.bidderRuns()?.find((br) => br.campaignType === this.campaignType);
    const nbHoursBeforeNextPush = Utils.estimatedNbHoursBeforeNextPush(
      Marketplaces[accountMarketplace.marketplace].platform,
      bidderRun?.bidderRequestTime ?? undefined,
      bidderRun?.lastBidderStart ?? undefined,
      bidderRun?.lastBidderEnd ?? undefined,
      lastUpdate!.timestamp!,
    );

    if (nbHoursBeforeNextPush && nbHoursBeforeNextPush > 0) {
      const updateMessage = lastUpdateIsStrategyCreation
        ? "strategy-page.update_hours"
        : "sp-strategy-group-page.time_for_update";
      return this.translocoService.translate(updateMessage, {
        count: nbHoursBeforeNextPush,
      });
    }
    return undefined;
  });

  brands = toSignal(
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am: AccountMarketplace) => this.sbStrategyService.getBrands(am.accountId, am.marketplace)),
    ),
  );
  isStrategyBrandDeleted = computed(() => {
    const strategy = this.strategy();
    return (
      this.accountMarketplace().accountType !== AccountType.VENDOR &&
      strategy &&
      strategy.isSbStrategy() &&
      !(this.brands() ?? []).find((b) => b.brandEntityId === strategy.brandEntityId)
    );
  });

  endOfMonth = computed<Date>(() => {
    const now = DateUtils.getNow(this.accountMarketplace().marketplace);
    return now.endOf("month").toJSDate();
  });
  currentMonthSpend = toSignal(
    this.strategy$.pipe(
      withLatestFrom(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$),
      switchMap(([s, am]) =>
        this.strategyService.getStrategyCurrentMonthSpend(s.accountId, s.marketplace, am.dspAdvertiserId, s.strategyId),
      ),
    ),
  );
  budgetFlightSpend = toSignal(
    this.strategy$.pipe(
      withLatestFrom(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$),
      switchMap(([s, am]) =>
        this.strategyService.getStrategyFlightSpend(s.accountId, s.marketplace, am.dspAdvertiserId, s.strategyId),
      ),
    ),
  );
  budgetFlightEndDate = computed<Date | undefined>(() => {
    const endDate = this.strategy()?.budgetFlight?.endDate;
    return endDate ? new Date(endDate) : undefined;
  });

  currencySymbol = computed(() =>
    this.strategy() ? CurrencySymbols[Marketplaces[this.strategy()!.marketplace].currency] : "",
  );
  currencyCode = computed(() => {
    if (this.campaignType === CampaignType.DSP) {
      return this.accountMarketplace().dspAdvertiser?.currency ?? Currency.USD;
    }
    return this.strategy() ? Marketplaces[this.strategy()!.marketplace].currency : Currency.USD;
  });

  newStrategyName: string = "";

  readonly fblMode = fblMode;
  readonly StrategyTypeStr = StrategyTypeStr;
  readonly DspFunnelDescriptions = DspFunnelDescriptions;
  AlgoModeStrKey = {
    [AlgoMode.PRODUCT_LAUNCH]: {
      description: "algo-mode-selection.force_product_visibility",
      shortDescription: "algo-mode-selection.constant_bid",
    },
    [AlgoMode.ACOS_TARGET]: {
      description: "algo-mode-selection.optimize_sales_with_acos_target",
      shortDescription: "algo-mode-selection.acos_target",
    },
    [AlgoMode.MONTHLY_BUDGET_TARGET]: {
      description: "algo-mode-selection.optimize_sales_with_monthly_budget_target",
      shortDescription: "algo-mode-selection.monthly_budget_target",
    },
    [AlgoMode.TACOS_TARGET]: {
      description: "algo-mode-selection.optimize_sales_with_tacos_target",
      shortDescription: "algo-mode-selection.tacos_target",
    },
    [AlgoMode.AMAZON_MANAGED_BUDGET]: {
      description: "algo-mode-selection.budget_allocation",
      shortDescription: "algo-mode-selection.budget_allocation",
    },
  };

  promoDaysBoostStart = toSignal(
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
      map((am: AccountMarketplace) => am.promoStartDate),
    ),
  );

  boostActivated$: Observable<boolean> = this.accountMarketplaceSelection.promoBoostActivated$;

  organizationId = computed(() => this.accountMarketplace().resourceOrganizationId);

  strategyTitleModal?: ModalRef<TemplateRef<any>>;

  strategyList = toSignal<StrategyEx[]>(
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
      combineLatestWith(toObservable(this.strategyGroup), this.strategy$),
      map(([stratIndex, strategyGroup, strategy]) => {
        const strategyList: StrategyEx[] = [];
        for (const [_, strat] of stratIndex) {
          if (
            strat.campaignType === strategy.campaignType &&
            strat.isSpStrategy() &&
            strat.strategyGroupId == strategyGroup?.strategyGroupId
          ) {
            strategyList.push(strat);
          }
        }
        return strategyList;
      }),
      startWith([]),
    ),
  );

  creatives = toSignal(
    this.strategy$.pipe(
      switchMap((s) => {
        if (s?.campaignType === CampaignType.SB) {
          return this.brandAssetsService
            .getSbCreativesPerStrategy(s.accountId, s.marketplace)
            .pipe(map((c) => c.get(s.strategyId) ?? []));
        }
        return of([]);
      }),
    ),
  );
  changeStatusLoading = signal(false);

  displayAdvancedSettingsSection = computed(() =>
    Object.values(this.strategyPageService.advancedSettings()).some((v) => v),
  );

  statusChangeDisabled(): boolean {
    if (
      this.strategy() &&
      this.strategy()?.state == StrategyStateEnum.PAUSED &&
      this.strategyService.isStrategyLimitReached(
        this.strategy()!.accountId,
        this.strategyList()!,
        this.strategy()!.campaignType,
      )
    ) {
      return true;
    }
    // disable activation for all other product for vendors
    return (
      !!this.strategy() &&
      this.strategy()!.state &&
      this.strategy()!.accountId.startsWith("ENTITY") &&
      this.strategy()!.campaignType == CampaignType.SP &&
      this.strategy()!.defaultStrategy
    );
  }

  changeStatus(): void {
    this.changeStatusLoading.set(true);
    const newState =
      this.strategy()!.state == StrategyStateEnum.ENABLED ? StrategyStateEnum.PAUSED : StrategyStateEnum.ENABLED;
    this.strategyService
      .updateStrategyState(
        this.strategy()!.accountId,
        this.strategy()!.marketplace,
        this.organizationId()!,
        this.strategy()!.strategyId,
        newState,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            newState == StrategyStateEnum.ENABLED
              ? this.translocoService.translate("strategy-grid.strategy_activated")
              : this.translocoService.translate("strategy-grid.strategy_paused"),
            this.translocoService.translate("strategy-page.strategy_status"),
          );
          this.changeStatusLoading.set(false);
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("strategy-page.strategy_status_error"));
          this.changeStatusLoading.set(false);
        },
      });
  }

  changeName(newName: string) {
    this.strategyService
      .updateStrategyName(
        this.strategy()!.accountId,
        this.strategy()!.marketplace,
        this.organizationId()!,
        this.strategy()!.strategyId,
        newName,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_name_updated"),
            this.translocoService.translate("strategy-page.success"),
          );
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("common.error"));
        },
      });

    this.strategyTitleModal?.close();
  }

  changeAlgoMode() {
    if (this.isReadOnly() || this.strategy()!.primeDayBoost || this.strategy()!.algoMode === AlgoMode.TACOS_TARGET)
      return;

    this.modalService.openModal(SwitchAlgoModeComponent, {
      modalTitle: "Change Strategy Algorithm",
      data: {
        organizationId: this.organizationId(),
        strategy: this.strategy(),
        currencyCode: this.currencyCode(),
      },
    });
  }

  editStrategyNameModal(template: TemplateRef<any>) {
    if (this.isReadOnly() || this.strategy()!.defaultStrategy) {
      return;
    }
    this.newStrategyName = this.strategy()!.name ?? "";
    this.strategyTitleModal = this.modalService.openModal(template, {
      modalTitle: this.translocoService.translate("strategy-page.update_strategy_name"),
    });
  }

  readonly AlgoModes = AlgoMode;
  readonly FblModeSize = FblModeSize;
  readonly TacticType = TacticType;
  readonly Section = Section;
}
